<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { useRoute } from "vue-router";
  import Navigation from "./components/Navigation.vue";
  import { Toaster } from "vue-sonner";
  import { useAuthStore } from "./stores/auth";
  import { useThemeStore } from "./stores/theme";

  const authStore = useAuthStore();
  const themeStore = useThemeStore();
  const showNavigation = computed(() => !!authStore.user && !route.meta.hideNavigation);

  const route = useRoute();
  const transitionName = ref("route-fade");
  const previousDepth = ref<number | null>(null);

  watch(
    () => route.fullPath,
    () => {
      const toDepth = Number(route.meta.depth ?? 0);
      if (previousDepth.value === null) {
        transitionName.value = "route-fade";
      } else if (toDepth > previousDepth.value) {
        transitionName.value = "slide-fade-right";
      } else if (toDepth < previousDepth.value) {
        transitionName.value = "slide-fade-left";
      } else {
        transitionName.value = "route-fade";
      }
      previousDepth.value = toDepth;
    },
    { immediate: true },
  );
</script>

<template>
  <div class="flex h-full w-full flex-col bg-(--p-background-0)">
    <ConfirmDialog />
    <Toaster
      position="top-center"
      :theme="themeStore.isDark ? 'dark' : 'light'"
      close-button
    />
    <Navigation v-if="showNavigation" />
    <router-view v-slot="{ Component, route }">
      <div class="mx-auto my-4 w-full max-w-screen-2xl px-2">
        <transition :name="transitionName">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </div>
    </router-view>
  </div>
</template>

<style>
  body {
    font-family: Arial, sans-serif;
    background-color: var(--p-background-50);
    color: var(--color-text-primary);
    min-height: 100vh;
  }

  .route-fade-enter-active,
  .route-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .route-fade-enter-from,
  .route-fade-leave-to {
    opacity: 0;
  }

  .slide-fade-right-enter-active,
  .slide-fade-right-leave-active,
  .slide-fade-left-enter-active,
  .slide-fade-left-leave-active {
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }

  .slide-fade-right-enter-from {
    opacity: 0;
    transform: translateX(16px);
  }

  .slide-fade-right-leave-to {
    opacity: 0;
    transform: translateX(-16px);
  }

  .slide-fade-left-enter-from {
    opacity: 0;
    transform: translateX(-16px);
  }

  .slide-fade-left-leave-to {
    opacity: 0;
    transform: translateX(16px);
  }
</style>
